import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaTree, 
  FaFemale, 
  FaHandHoldingHeart, 
  FaHome,
  FaSeedling,
  FaHandsHelping,
  FaGraduationCap,
  FaHospital,
  FaBalanceScale,
  FaLeaf,
  FaWater,
  FaUsers,
  FaChartLine,
  FaBook,
  FaCheckCircle,
  FaDove,
  FaQrcode
} from 'react-icons/fa';
import { GiMeditation, GiPlantWatering } from 'react-icons/gi';
import { MdOutlineElderly } from 'react-icons/md';
import { useLanguage } from '../context/LanguageContext';

const ServicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedServiceForDonation, setSelectedServiceForDonation] = useState<string>('');
  
  const services = [
    {
      id: 'tree',
      icon: <img src="/envconservation.jpeg" alt="Environmental Conservation" className="w-full h-full object-contain" />,
      title: ' Environmental Conservation',
      description: 'To protect Environment through promotion of forest (Trees) plantation.',
      color: 'bg-green-50 border-green-100',
      iconColor: 'text-primary-green',
      details: [
        'Environmental protection campaigns',
        'Community plantation drives',
        'Volunteer registration for green initiatives',
        'Plantation monitoring and maintenance',
        'Awareness programs on climate change'
      ],
      impact: '50,000+ trees planted across 100+ locations'
    },
    {
      id: 'cow',
      icon: <img src="/cowwelfare.png" alt="Cow Welfare" className="w-full h-full object-contain" />,
      title: 'Animal Welfare & Shelters',
      description: 'Open Cow, Goat and sheep sheds and also promote Animal Husbandry.',
      color: 'bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 border-amber-200',
      iconColor: 'text-amber-600',
      details: [
        'Cow shelter establishment and maintenance',
        'Emergency animal rescue operations',
        'Cow adoption and sponsorship programs',
        'Veterinary care and health treatment',
        'Animal welfare education and awareness'
      ],
      impact: '1,200+ cows rescued and rehabilitated'
    },
    {
      id: 'girls',
      icon: <img src="/orphangehosuse.jpg" alt="Orphanage House" className="w-full h-full object-contain" />,
      title: ' Orphanage House',
      description: 'Empowering girls through education and skill development',
      color: 'bg-pink-50 border-pink-100',
      iconColor: 'text-pink-500',
      details: [
        'Education support and scholarships',
        'Skill development programs',
        'Health and hygiene awareness campaigns',
        'Career counseling and mentorship',
        'Digital literacy programs'
      ],
      impact: '2,500+ girls supported with education and skills'
    },
    {
      id: 'disaster',
      icon: <img src="/naturaldisater.jpg" alt="Natural Disaster Relief" className="w-full h-full object-contain" />,
      title: ' Natural Disaster Relief',
      description: 'Provide help in natural disasters through our Volunteers.',
      color: 'bg-blue-50 border-blue-100',
      iconColor: 'text-blue-500',
      details: [
        'Emergency rescue operations',
        'Food and essential supplies distribution',
        'Medical assistance and camps',
        'Temporary shelter arrangements',
        'Volunteer mobilization for relief work'
      ],
      impact: '150+ disaster relief campaigns conducted'
    },
    {
      id: 'oldage',
      icon: <img src="/oldage.png" alt="Orphan & Old Age Integration" className="w-full h-full object-contain" />,
      title: ' Orphan & Old Age Integration',
      description: 'To open Orphanage house and old age home.',
      color: 'bg-purple-50 border-purple-100',
      iconColor: 'text-purple-500',
      details: [
        'Elderly care and companionship programs',
        'Health checkups and medical support',
        'Daily living assistance',
        'Recreational and social activities',
        'Sponsorship opportunities for elderly care'
      ],
      impact: '800+ elderly residents supported'
    },
    {
      id: 'meditation',
      icon: <img src="/meditation.jpg" alt="Self-Knowledge & Meditation" className="w-full h-full object-contain" />,
      title: ' Self-Knowledge & Meditation',
      description: 'To open Institution for Research and Development in Meditation.',
      color: 'bg-indigo-50 border-indigo-100',
      iconColor: 'text-indigo-500',
      details: [
        'Meditation centers and retreats',
        'Research projects on mental wellness',
        'Wellness programs for stress management',
        'Training sessions and workshops',
        'Publications and research reports'
      ],
      impact: '10,000+ individuals benefited from meditation programs'
    },
    {
      id: 'heritage',
      icon: <img src="/historical.png" alt="Heritage & Historical Places" className="w-full h-full object-contain" />,
      title: 'Heritage (historical) & Old devotional places development',
      description: 'Research and development on Indian Historical and Devotional places and provide help in their maintenance.',
      color: 'bg-yellow-50 border-yellow-100',
      iconColor: 'text-yellow-600',
      details: [
        'Preservation of historical places',
        'Temple maintenance and restoration projects',
        'Cultural awareness programs',
        'Traditional art forms promotion',
        'Heritage documentation and research'
      ],
      impact: '50+ heritage sites preserved and maintained'
    },
    {
      id: 'legal',
      icon: <img src="/leagalaid.webp" alt="Legal Aid Program" className="w-full h-full object-contain" />,
      title: 'Legal Aid Program',
      description: 'To provide free legal advice and assistance through Retired Judges/Senior Advocates to needy and financially weak persons.',
      color: 'bg-red-50 border-red-100',
      iconColor: 'text-red-500',
      details: [
        'Free legal consultation services',
        'Legal awareness camps',
        'Assistance for needy families',
        'Social justice initiatives',
        'Advocacy for marginalized communities'
      ],
      impact: '5,000+ individuals provided with legal aid'
    },
    {
      id: 'organic',
      icon: <img src="/organic.jpg" alt="Organic Farming & Ayurveda" className="w-full h-full object-contain" />,
      title: 'Organic Farming & Ayurveda R&D',
      description: 'Organic and herb Plantation and promote Ayurveda.',
      color: 'bg-teal-50 border-teal-100',
      iconColor: 'text-teal-500',
      details: [
        'Organic plantation projects',
        'Ayurvedic awareness programs',
        'Sustainable agriculture training',
        'Medicinal plant cultivation',
        'Farmer empowerment initiatives'
      ],
      impact: '500+ farmers trained in organic farming techniques'
    }
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary-green/20 via-primary-blue/10 to-white py-16 md:py-20 lg:py-24 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-gradient-to-br from-primary-green/15 to-primary-blue/15 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-tr from-primary-orange/10 to-primary-green/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1.5s" }}></div>
        
        <div className="container-lg px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.span 
              className="inline-block px-4 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-primary-green/30 to-primary-blue/30 rounded-full text-primary-green font-bold text-xs sm:text-sm mb-4 sm:mb-6 backdrop-blur-sm border border-primary-green/30 shadow-lg shadow-primary-green/10"
              whileHover={{ scale: 1.05 }}
            >
              🌟 {t('transformingLives')}
            </motion.span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black bg-gradient-to-r from-gray-900 via-primary-green to-gray-900 bg-clip-text text-transparent mb-4 sm:mb-6 leading-tight">{t('ourServicesCauses')}</h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-medium px-4">
              {t('servicesSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-gradient-to-b from-white to-gray-50">
        <div className="container-lg px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: index * 0.15, ease: "easeOut" }}
                viewport={{ once: true, margin: "-50px" }}
                whileHover={{ y: -16 }}
                className="h-full group"
              >
                <div className={`${service.color} rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-500 h-full flex flex-col border-2 border-opacity-60 backdrop-blur-xl relative group-hover:bg-opacity-90 before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/50 before:to-transparent before:opacity-0 group-hover:before:opacity-100 before:transition-opacity before:duration-400`}>
                  {/* Premium Animated Background */}
                  <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/20 rounded-full blur-3xl group-hover:scale-125 transition-transform duration-500 group-hover:animate-pulse"></div>
                  <div className="absolute -bottom-32 -left-32 w-64 h-64 bg-gradient-to-br from-primary-green/15 to-primary-blue/15 rounded-full blur-2xl group-hover:scale-110 transition-transform duration-500"></div>
                  
                  {/* Premium Header */}
                  <div className="relative p-10 text-center overflow-hidden group-hover:bg-white/10 transition-all duration-400">
                    {/* Background gradient effects */}
                    <div className="absolute inset-0 opacity-5 group-hover:opacity-20 transition-opacity duration-400"></div>
                    
                    {/* Decorative corner badges */}
                    <div className="absolute top-4 right-4 w-10 h-10 bg-gradient-to-br from-primary-green/25 to-primary-blue/25 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 shadow-lg shadow-primary-green/20"></div>
                    <div className="absolute top-4 left-4 w-8 h-8 bg-gradient-to-br from-primary-orange/20 to-primary-green/20 rounded-full opacity-0 group-hover:opacity-75 transition-opacity duration-500 animate-pulse"></div>
                    
                    <div className="relative z-10 mb-4 sm:mb-6 flex justify-center">
                      <div 
                        className={`text-6xl sm:text-7xl md:text-8xl lg:text-9xl ${service.iconColor} drop-shadow-2xl filter transition-all duration-500 group-hover:brightness-150`}
                      >
                        {service.icon}
                      </div>
                    </div>
                    
                    <h3 className="text-xl sm:text-2xl font-black text-gray-900 relative z-10 group-hover:text-primary-green transition-colors duration-300 leading-tight min-h-[4.5rem] flex items-center justify-center text-center">{service.title}</h3>
                  </div>

                  {/* Premium Divider */}
                  <div className="mx-6 h-1.5 bg-gradient-to-r from-transparent via-primary-green/40 to-transparent opacity-50 group-hover:opacity-100 group-hover:via-primary-green transition-all duration-300\"></div>

                  {/* Content */}
                  <div className="p-8 flex-1 flex flex-col space-y-6">
                    <p className="text-gray-700 text-base leading-relaxed font-medium">{service.description}</p>
                    
                    {/* Key Activities */}
                    <div>
                      <div className="flex items-center gap-2 mb-4 pb-2 border-b-2 border-primary-green/20">
                        <span className="text-primary-green text-xl"><FaChartLine /></span>
                        <h4 className="font-bold text-gray-900 text-lg">{t('keyActivities')}</h4>
                      </div>
                      <ul className="space-y-3">
                        {service.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start text-sm gap-3">
                            <span className="text-primary-green mt-1 flex-shrink-0 text-base"><FaCheckCircle /></span>
                            <span className="text-gray-700 font-medium leading-relaxed">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Impact Section - Premium Card */}
                    <motion.div 
                      className="mt-4 p-6 rounded-2xl bg-gradient-to-br from-primary-green/15 via-white to-primary-blue/15 border-2 border-primary-green/30 shadow-lg hover:shadow-xl hover:border-primary-green/50 transition-all duration-300"
                      whileHover={{ y: -4 }}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <motion.span 
                          className="text-2xl text-primary-green"
                          animate={{ rotate: [0, 10, 0] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          <FaSeedling />
                        </motion.span>
                        <h4 className="font-bold text-gray-900 text-lg">{t('impactAchieved')}</h4>
                      </div>
                      <p className="text-primary-green font-black text-2xl leading-tight">{service.impact}</p>
                    </motion.div>
                    
                    {/* Premium Button */}
                    <motion.button
                      onClick={() => {
                        setSelectedServiceForDonation(service.id);
                        setShowQRModal(true);
                      }}
                      whileHover={{ scale: 1.08, y: -4 }}
                      whileTap={{ scale: 0.93 }}
                      className="w-full mt-6 bg-gradient-to-r from-primary-green via-emerald-500 to-primary-blue text-white font-bold py-4 rounded-xl shadow-xl hover:shadow-2xl hover:shadow-primary-green/40 transition-all duration-300 flex items-center justify-center gap-3 text-lg group/btn border border-primary-green/60 hover:border-primary-green/100 relative overflow-hidden"
                    >
                      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover/btn:translate-x-full transition-transform duration-500"></span>
                      <motion.span 
                        className="text-2xl relative z-10"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                      >
                    
                      </motion.span>
                      <span className="relative z-10 font-semibold">Donate Us</span>
                      <motion.span 
                        className="group-hover/btn:translate-x-2 transition-transform relative z-10"
                        animate={{ x: [0, 4, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        →
                      </motion.span>
                    </motion.button>
                  </div>

                  {/* Decorative top and bottom accents with glow */}
                  <div className="h-1.5 bg-gradient-to-r from-primary-green via-primary-blue to-primary-orange opacity-0 group-hover:opacity-100 transition-opacity duration-400 shadow-lg shadow-primary-green/20 absolute top-0 left-0 right-0"></div>
                  <motion.div 
                    className="h-1.5 bg-gradient-to-r from-primary-orange via-primary-green to-primary-blue opacity-0 group-hover:opacity-100 transition-opacity duration-400 shadow-lg shadow-primary-green/20 absolute bottom-0 left-0 right-0"
                  ></motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Get Involved */}
      <section className="section-padding bg-gray-50">
        <div className="container-lg px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('howGetInvolved')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('howGetInvolvedDesc')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-lg p-6 text-center"
            >
              <div className="text-5xl text-primary-green mb-4 mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <FaHandHoldingHeart />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">{t('donateTitle')}</h3>
              <p className="text-gray-600 mb-4">
                {t('donateDesc')}
              </p>
              <button 
                onClick={() => {
                  setSelectedServiceForDonation('general');
                  setShowQRModal(true);
                }}
                className="btn-primary w-full"
              >
                {t('donate')}
              </button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-lg p-6 text-center"
            >
              <div className="text-5xl text-primary-orange mb-4 mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                <FaHandsHelping />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">{t('volunteerTitle')}</h3>
              <p className="text-gray-600 mb-4">
                {t('volunteerDesc')}
              </p>
              <button className="btn-secondary w-full">
                {t('joinAsVolunteer')}
              </button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-lg p-6 text-center"
            >
              <div className="text-5xl text-primary-blue mb-4 mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <FaUsers />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">{t('partnerTitle')}</h3>
              <p className="text-gray-600 mb-4">
                {t('partnerDesc')}
              </p>
              <button className="btn-accent w-full">
                {t('partnerWithUs')}
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Categories Summary */}
      <section className="section-padding">
        <div className="container-lg px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('ourFocusAreas')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('focusAreasDesc')}
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <span className="text-3xl text-primary-green mx-auto mb-3 flex justify-center"><FaTree /></span>
              <div className="font-semibold text-gray-800">{t('environmental')}</div>
              <div className="text-sm text-gray-600">{t('environmentalDesc')}</div>
            </div>
            
            <div className="bg-pink-50 rounded-xl p-4 text-center">
              <span className="text-3xl text-pink-500 mx-auto mb-3 flex justify-center"><FaFemale /></span>
              <div className="font-semibold text-gray-800">{t('education')}</div>
              <div className="text-sm text-gray-600">{t('educationDesc')}</div>
            </div>
            
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <span className="text-3xl text-blue-500 mx-auto mb-3 flex justify-center"><FaHandHoldingHeart /></span>
              <div className="font-semibold text-gray-800">{t('humanitarian')}</div>
              <div className="text-sm text-gray-600">{t('humanitarianDesc')}</div>
            </div>
            
            <div className="bg-purple-50 rounded-xl p-4 text-center">
              <span className="text-3xl text-purple-500 mx-auto mb-3 flex justify-center"><FaBalanceScale /></span>
              <div className="font-semibold text-gray-800">{t('legalCultural')}</div>
              <div className="text-sm text-gray-600">{t('legalCulturalDesc')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* QR Code Modal */}
      {showQRModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
          onClick={() => setShowQRModal(false)}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden relative my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowQRModal(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors text-gray-600 hover:text-gray-800"
            >
              <span className="text-2xl leading-none">×</span>
            </button>

            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary-green to-primary-blue p-6 text-white text-center">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-5xl mb-3"
              >
                <FaQrcode className="mx-auto" />
              </motion.div>
              <h3 className="text-2xl font-bold mb-2">Scan & Donate</h3>
              <p className="text-white/90 text-sm">
                {services.find(s => s.id === selectedServiceForDonation)?.title}
              </p>
            </div>

            {/* QR Code Display */}
            <div className="p-6">
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-4 shadow-inner">
                <div className="w-full max-w-md mx-auto aspect-square flex items-center justify-center bg-white rounded-xl shadow-lg p-4">
                  <img 
                    src="/qrscanner.png" 
                    alt="QR Code for Donation" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* UPI ID Only */}
              <div className="mt-4 text-center">
                <div className="font-bold text-gray-800 text-lg mb-1">UPI ID</div>
                <div className="text-primary-green font-semibold text-xl">vishwabandhuwelfaref.82170069@hdfcbank</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default ServicesPage;